import { useState } from 'react';
import Layout from '../../../components/feature/Layout';

interface Report {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
}

export default function CRMReportsPage() {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);

  const reports: Report[] = [
    {
      id: '1',
      name: 'Clientes Activos',
      description: 'Listado completo de clientes con estado activo',
      icon: 'ri-user-line',
      category: 'Clientes'
    },
    {
      id: '2',
      name: 'Clientes Nuevos',
      description: 'Clientes registrados en el último mes',
      icon: 'ri-user-add-line',
      category: 'Clientes'
    },
    {
      id: '3',
      name: 'Clientes Inactivos',
      description: 'Clientes sin operaciones en los últimos 90 días',
      icon: 'ri-user-unfollow-line',
      category: 'Clientes'
    },
    {
      id: '4',
      name: 'Oportunidades por Etapa',
      description: 'Distribución de oportunidades en el pipeline',
      icon: 'ri-pie-chart-line',
      category: 'Ventas'
    },
    {
      id: '5',
      name: 'Facturación por Cliente',
      description: 'Total facturado por cliente en el período',
      icon: 'ri-money-dollar-circle-line',
      category: 'Finanzas'
    },
    {
      id: '6',
      name: 'Ranking de Clientes',
      description: 'Top clientes por volumen de operaciones',
      icon: 'ri-trophy-line',
      category: 'Análisis'
    },
    {
      id: '7',
      name: 'Seguimientos Realizados',
      description: 'Actividades comerciales por ejecutivo',
      icon: 'ri-task-line',
      category: 'Actividades'
    },
    {
      id: '8',
      name: 'Incidencias por Cliente',
      description: 'Historial de reclamos y resoluciones',
      icon: 'ri-alert-line',
      category: 'Servicio'
    }
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2A43]">Reportes CRM</h1>
          <p className="text-sm text-gray-500 mt-1">Análisis y reportes del módulo de gestión de clientes</p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Clientes Activos</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">48</p>
                <p className="text-xs text-green-600 mt-1">↑ 12% vs mes anterior</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-user-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Oportunidades Activas</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">23</p>
                <p className="text-xs text-blue-600 mt-1">Q 2.4M en pipeline</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-line-chart-line text-2xl text-blue-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Facturación Mensual</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">Q 850K</p>
                <p className="text-xs text-green-600 mt-1">↑ 8% vs mes anterior</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-2xl text-purple-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tasa de Conversión</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">68%</p>
                <p className="text-xs text-green-600 mt-1">↑ 5% vs mes anterior</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <i className="ri-percent-line text-2xl text-yellow-600"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Catálogo de Reportes */}
        <div className="bg-white rounded-lg shadow-[0_4px_16px_rgba(10,42,67,0.08)]">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Reportes Disponibles</h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => {
                    setSelectedReport(report);
                    setShowPreviewModal(true);
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${report.icon} text-2xl text-blue-600`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{report.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">{report.description}</p>
                      <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {report.category}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Modal de Vista Previa */}
        {showPreviewModal && selectedReport && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedReport.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{selectedReport.description}</p>
                </div>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Parámetros del Reporte */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Parámetros del Reporte</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        defaultValue="2024-01-01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        defaultValue="2024-01-31"
                      />
                    </div>
                  </div>
                </div>

                {/* Vista Previa de Datos */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Vista Previa</h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Operaciones</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Facturación</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        <tr>
                          <td className="px-4 py-3 text-sm text-gray-900">Importadora Global S.A.</td>
                          <td className="px-4 py-3 text-sm text-gray-600">CLI-001</td>
                          <td className="px-4 py-3 text-sm text-center text-gray-900">24</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900">Q 285,000</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 text-sm text-gray-900">Exportadora del Pacífico</td>
                          <td className="px-4 py-3 text-sm text-gray-600">CLI-002</td>
                          <td className="px-4 py-3 text-sm text-center text-gray-900">18</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900">Q 195,000</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 text-sm text-gray-900">Logística Continental</td>
                          <td className="px-4 py-3 text-sm text-gray-600">CLI-003</td>
                          <td className="px-4 py-3 text-sm text-center text-gray-900">32</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900">Q 370,000</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-between">
                <div className="flex gap-2">
                  <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
                    <i className="ri-file-pdf-line"></i>
                    Exportar PDF
                  </button>
                  <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2">
                    <i className="ri-file-excel-line"></i>
                    Exportar Excel
                  </button>
                  <button className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2">
                    <i className="ri-file-text-line"></i>
                    Exportar CSV
                  </button>
                </div>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
